"use client";
import { signInAction } from "@/app/actions";
import { customerioIdentify } from "@/app/actions/customerio";
import { getUser } from "@/app/hooks/getUser";
import { Loading } from "@/app/template/frontend/global/Loading";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { createClient } from "@/utils/supabase/client";
import Cookies from "js-cookie";
import { CheckCircle2, MailIcon } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { useEffect, useState, useTransition } from "react";
import { toast } from "@/lib/helper/ui/toast";

const SHOPIFY_TOKEN_KEY = 'kinetic_shopify_token';

export function LoginForm({ className, ...props }: React.ComponentProps<"div">) {
  const { user, loading } = getUser() as { user: { email: string } | null, loading: boolean };
  const searchParams = useSearchParams();
  const email = searchParams.get('email');
  const verified = searchParams.get('verified');
  const status = searchParams.get('status');
  const router = useRouter();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [messageType, setMessageType] = useState<"error" | "success" | "info" | "warning">("info");
  const [loginButtonLabel, setLoginButtonLabel] = useState<React.ReactNode>("Login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  let [isPending, startTransition] = useTransition();

  const supabase = createClient();

  const shopifyToken = searchParams.get("token");
  const klaviyoOauth = searchParams.get("klaviyo_oauth");
  const redirectTo = searchParams.get("redirect_to");
  const inviteToken = searchParams.get("invite");

  // Store token in sessionStorage and check for existing token
  useEffect(() => {
    if (shopifyToken) {
      sessionStorage.setItem(SHOPIFY_TOKEN_KEY, shopifyToken);
      setCurrentToken(shopifyToken);
    } else {
      // Check sessionStorage for existing token
      const storedToken = sessionStorage.getItem(SHOPIFY_TOKEN_KEY);
      if (storedToken) {
        setCurrentToken(storedToken);
      }
    }
  }, [shopifyToken]);

  // Build URLs with token preserved
  const signupUrl = currentToken ? `/onboarding?token=${currentToken}&new=1` : '/onboarding?new=1';
  const forgotPasswordUrl = currentToken ? `/forgot-password?token=${currentToken}` : '/forgot-password';

  console.log("shopifyToken", currentToken);

  useEffect(() => {
    if (verified === 'true' && email) {
      toast.success('Email verified successfully! Please log in to continue.');
    }
  }, [verified, email]);

  useEffect(() => {
    // Send toast jf shccess
    if (status === "success") {
      toast.success("Email confirmed successfully! Please login to continue.");
    }
  }, [status]);

  // Check for success messages from URL params
  useEffect(() => {
    // Show hint when coming from OAuth authorize flow
    if (klaviyoOauth === "login_required") {
      toast.info("To connect your Klaviyo account, please log into Kinetic first.");
    }

    const signup = searchParams.get("signup");
    const magiclink = searchParams.get("magiclink");
    const signupConfirmed = searchParams.get("signup_confirmed");

    if (signupConfirmed === "1") {
      setSuccessMessage("Your email has been confirmed successfully! You can now log in to your account.");
    } else if (signup === "success") {
      setSuccessMessage("Account created successfully! Please check your email to verify your account.");
    } else if (magiclink === "sent") {
      setSuccessMessage("Magic link sent! Please check your email to log in.");
    }
  }, [searchParams]);

  const handleLogin = async (event: {
    preventDefault: () => void;
    currentTarget: HTMLFormElement | undefined;
  }) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const formData = new FormData(event.currentTarget);

    if (currentToken) {
      formData.append("shopifyToken", currentToken);
    }

    startTransition(async () => {
      try {
        const result = await signInAction(formData);

        const email = formData.get("email") as string;


        // Check if Customer.io is available before calling identify


        if (typeof window !== 'undefined' && window.cioanalytics) {
          console.log("Customer.io is available");
          console.log(window.cioanalytics);
          window.cioanalytics.debug();
        }

        const userId = await supabase.auth.getSession().then(({ data }) => {
          return data.session?.user.id;
        });



        customerioIdentify(email, {
          email: email,
          id: userId,
          created_at: new Date().toISOString(),
        })

        await posthog.identify(email)

        if (200 !== result.status) {
          setErrorMessage(result.error ?? "Invalid email or password");
        } else {
          // Clear token from sessionStorage on successful login
          sessionStorage.removeItem(SHOPIFY_TOKEN_KEY);

          const company_id = result.company_id;
          const store_name = result.store_name;
          // Save company id to cookie
          Cookies.set("company_id", company_id, { expires: 365, path: "/" });
          Cookies.set("shopify_store_name", store_name, { expires: 365, path: "/" });
          // Redirect on successful login
          if (inviteToken) {
            // Redirect to signup page which handles invite acceptance for logged-in users
            router.replace(`/signup?invite=${inviteToken}`);
          } else if (redirectTo) {
            // Use replace so browser back does not land on login again
            router.replace(redirectTo);
          } else if (result.from_shopify) {
            router.push("/dashboard?shopify=success");
          } else {
            router.push("/dashboard");
          }
          setErrorMessage("");
        }
      } catch (error) {
        console.error("Error handling login:", error);
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    });
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <div>
            {user ? (
              <>
                <div className="flex flex-col gap-6 p-4 px-8 min-h-[400px] align-center justify-center">
                  <div className="flex flex-col items-center text-center">
                    <h1 className="text-2xl font-bold">Hello! 👋</h1>
                    <p className="text-balance text-muted-foreground">
                      You are already logged in as <br />
                      {user.email}.
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <Button
                      onClick={() => router.push("/dashboard")}
                      className="w-full"
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                  <div className="text-center text-sm">
                    <p className="text-muted-foreground">
                      Not you?{" "}
                      <a
                        href="#"
                        onClick={async () => {
                          await supabase.auth.signOut();
                          router.refresh();
                        }}
                        className="underline underline-offset-4"
                      >
                        Sign out
                      </a>
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <form onSubmit={handleLogin} className="p-6 md:p-8">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col items-center text-center">
                    <h1 className="text-2xl font-bold">Welcome back</h1>
                    <p className="text-balance text-muted-foreground">
                      Login to your Kinetic account.
                    </p>
                  </div>

                  {inviteToken && (
                    <Alert variant="default" className="flex items-center gap-2 border-blue-200 bg-blue-50">
                      <MailIcon className="h-4 w-4 text-blue-600 shrink-0" />
                      <p className="text-sm text-blue-800">Log in to accept your workspace invitation.</p>
                    </Alert>
                  )}

                  {errorMessage && (
                    <Alert variant="destructive">
                      <p>{errorMessage}</p>
                    </Alert>
                  )}

                  {successMessage && (
                    <Alert variant="default" className="flex items-center gap-2 border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <p>{successMessage}</p>
                    </Alert>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      required
                      disabled={isSubmitting}
                      defaultValue={email || ""}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="•••••••••••"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? <Loading hideText={true} /> : "Login"}
                  </Button>

                  <div className="text-center text-sm">
                    <a
                      href={forgotPasswordUrl}
                      className="text-muted-foreground underline-offset-2 hover:underline hover:text-foreground"
                    >
                      Forgot your password?
                    </a>
                  </div>

                  <div className="text-center text-sm">
                    Don&apos;t have an account?{" "}
                    <Link
                      href={signupUrl}
                      className="underline underline-offset-4"
                    >
                      Sign up
                    </Link>
                  </div>
                </div>
              </form>
            )}
      </div>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By using Kinetic, you agree to our <a href="https://www.usekinetic.com/terms">Terms of Service</a>{" "}
        and <a href="https://www.usekinetic.com/privacy">Privacy Policy</a>.
      </div>
    </div>
  );
}
